import { useEffect, useRef } from "react";
import { GameAdapter, GameAdapterRef } from "./game-adapter";
import { usePlay } from "@/providers/play-provider";
import { useAuth } from "@/providers/auth-provider";
import { GameMetadata } from "./game-registry";

interface OnlineGameAdapterProps {
  game: GameMetadata;
}

export const OnlineGameAdapter = ({ game }: OnlineGameAdapterProps) => {
  const { user } = useAuth();
  const { currentRoom, sendGameAction, sendGameState } = usePlay();
  const gameAdapterRef = useRef<GameAdapterRef>(null);

  const sendMessageToGame = (message: any) => {
    if (gameAdapterRef.current) {
      gameAdapterRef.current.sendMessageToGame(message);
    }
  };

  // 연결 상태 이벤트 처리
  useEffect(() => {
    const handleConnectionLost = () => {
      sendMessageToGame({ type: "online.connectionLost" });
    };

    const handleReconnected = () => {
      if (currentRoom) {
        sendMessageToGame({
          type: "online.reconnected",
          data: { room: currentRoom, playerId: user?._id },
        });
      }
    };

    window.addEventListener("connectionLost", handleConnectionLost);
    window.addEventListener("reconnected", handleReconnected);

    return () => {
      window.removeEventListener("connectionLost", handleConnectionLost);
      window.removeEventListener("reconnected", handleReconnected);
    };
  }, [currentRoom, user]);

  // 서버 이벤트 수신 처리
  useEffect(() => {
    const handleServerEvent = (event: CustomEvent) => {
      console.log(`[OnlineGameAdapter:${game.id}] Server Event:`, event);
      const { action, actionData, playerId } = event.detail;
      sendMessageToGame({
        type: "online.gameEvent",
        data: { action, actionData, playerId },
      });
    };

    const handleServerState = (event: CustomEvent) => {
      console.log(`[OnlineGameAdapter:${game.id}] State Update:`, event);
      sendMessageToGame({
        type: "online.gameState",
        data: event.detail,
      });
    };

    window.addEventListener(
      "onlineGameAction",
      handleServerEvent as EventListener,
    );
    window.addEventListener(
      "onlineGameStateUpdate",
      handleServerState as EventListener,
    );

    return () => {
      window.removeEventListener(
        "onlineGameAction",
        handleServerEvent as EventListener,
      );
      window.removeEventListener(
        "onlineGameStateUpdate",
        handleServerState as EventListener,
      );
    };
  }, [game.id, user?._id]);

  const handleGameEvent = async (event: any) => {
    console.log(`[OnlineGameAdapter:${game.id}] Game Event:`, event);
    // 게임 이벤트를 서버로 전송
    switch (event.id) {
      case "game.init":
        // 게임이 준비되면 방의 정보를 게임에 전달.
        console.log("[OnlineGameAdapter] 게임 초기화:", event);
        if (currentRoom) {
          sendMessageToGame({
            type: "online.gameInit",
            data: {
              room: currentRoom,
              playerId: user?._id,
              playerName: user?.name,
              players: currentRoom.players,
              gameState: currentRoom.gameState || {},
              isFirstPlayer: currentRoom.players[0]?.id === user?._id,
            },
          });
        } else {
          console.error("[OnlineGameAdapter] 방이 없습니다.");
        }
        return true;

      case "game.event":
        // 서버로 게임 이벤트 전송.(Broadcast)
        console.log("[OnlineGameAdapter] 게임 이벤트 전송:", event);
        sendGameAction(event.type, event);
        return true;

      case "game.state":
        // 서버로 게임 상태 전송.(Broadcast)
        console.log("[OnlineGameAdapter] 게임 상태 업데이트:", event);
        sendGameState(event);
        return true;
    }
    console.log("[OnlineGameAdapter] 처리되지 않은 게임 이벤트:", event);
    return false;
  };

  if (!currentRoom) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-muted-foreground">게임방 정보를 불러오는 중...</p>
      </div>
    );
  }

  return (
    <GameAdapter
      ref={gameAdapterRef}
      game={game}
      mode="online"
      onGameEvent={handleGameEvent}
      className="w-full h-full"
    />
  );
};
